/**
 * selling/EditAdModal.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Modal for editing an existing listing. Opened when the seller clicks
 * on one of their own ad cards in the SellingTab grid.
 *
 * FEATURES:
 *   - Edit title, description, price, location, category
 *   - Upload additional images / videos
 *   - Toggle ad status (Active ↔ Sold)
 *   - Delete ad (with confirmation)
 *   - Repost ad after deletion (Business plan only)
 *   - Show buyer messages for this specific ad
 *
 * PROPS:
 *   product         {object}   — the ad being edited
 *   onClose         {function} — close without saving
 *   onUpdated       {function} — called after successful update
 *   onDeleted       {function} — called after successful delete
 *   onStatusToggled {function} — called after status toggle
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useRef } from 'react';
import styles from './SellingTab.module.css';
import { FALLBACK_IMG, handleImgError, isVideoUrl, extractUrl, Spinner } from './sellingUtils';

import apiService from '../../../services/api';
const API_BASE = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';
function getToken() {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
}

const EditAdModal = ({ product, onClose, onUpdated, onDeleted, onStatusToggled }) => {
  const updateAd = (...args) => apiService.updateAd(...args);
  const deleteAd = (...args) => apiService.deleteAd(...args);

  const [editPrice, setEditPrice] = useState(String(product.price || ''));
  const [editName, setEditName] = useState(product.name || '');
  const [editLocation, setEditLocation] = useState(product.city || product.location || '');
  const [savingPrice, setSavingPrice] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [mediaIndex, setMediaIndex] = useState(0);

  const isSold = product.status === 'sold';
  const anyBusy = savingPrice || togglingStatus || deleting;

  // Build media array (videos first, then images)
  const mediaArray = React.useMemo(() => {
    const videos = [];
    const images = [];

    if (isVideoUrl(extractUrl(product.img))) videos.push(extractUrl(product.img));
    if (product.videos && Array.isArray(product.videos)) {
      product.videos.forEach(v => {
        const vUrl = extractUrl(v); if (isVideoUrl(vUrl) && !videos.includes(vUrl)) videos.push(vUrl);
      });
    }
    if (product.images && Array.isArray(product.images)) {
      product.images.forEach(img => {
        const iUrl = extractUrl(img);
        if (isVideoUrl(iUrl) && !videos.includes(iUrl)) videos.push(iUrl);
      });
    }

    if (!isVideoUrl(extractUrl(product.img)) && product.img) images.push(extractUrl(product.img) || product.img);
    if (product.images && Array.isArray(product.images)) {
      product.images.forEach(img => {
        const iUrl = extractUrl(img);
        if (!isVideoUrl(iUrl) && iUrl && !images.includes(iUrl)) images.push(iUrl);
      });
    }
    if (product.image && !isVideoUrl(product.image) && !images.includes(product.image)) {
      images.push(product.image);
    }

    return [...videos, ...images].length > 0 ? [...videos, ...images] : [FALLBACK_IMG];
  }, [product]);

  const currentMedia = mediaArray[mediaIndex] || FALLBACK_IMG;
  const isCurrentVideo = isVideoUrl(currentMedia);
  const totalMedia = mediaArray.length;

  const goToPrevious = () => {
    setMediaIndex(prev => (prev > 0 ? prev - 1 : totalMedia - 1));
  };

  const goToNext = () => {
    setMediaIndex(prev => (prev < totalMedia - 1 ? prev + 1 : 0));
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const handleSave = async () => {
    const price = parseFloat(editPrice);
    if (!editPrice || isNaN(price) || price <= 0) {
      showToast('Please enter a valid price', 'error');
      return;
    }
    setSavingPrice(true);
    try {
      const payload = {
        ...product,
        price,
        name: editName.trim() || product.name,
        city: editLocation.trim() || product.city,
        location: editLocation.trim() || product.location,
      };
      await updateAd(product.id, payload);
      onUpdated?.(payload);
      showToast('Listing updated successfully!');
    } catch (err) {
      console.error('updateAd:', err);
      showToast(err?.message || 'Failed to save changes', 'error');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleToggleStatus = async () => {
    setTogglingStatus(true);
    const newStatus = isSold ? 'active' : 'sold';
    try {
      const payload = { ...product, status: newStatus };
      await updateAd(product.id, payload);
      onStatusToggled?.(payload);
      showToast(`Marked as ${newStatus === 'sold' ? 'Sold' : 'Active'}!`);
    } catch (err) {
      console.error('toggleStatus:', err);
      showToast(err?.message || 'Failed to update status', 'error');
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (!deleteAd || typeof deleteAd !== 'function') {
        throw new Error('Delete function is not available. Please check your API configuration.');
      }

      const result = await deleteAd(product.id, product);
      onDeleted?.(product);

      if (result.promotion_created) {
        showToast(`✅ Listing deleted! 🎁 Re-post promotion created - check your promotions!`);
      } else {
        showToast('Listing deleted successfully!');
      }

      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error('deleteAd error:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'Failed to delete listing. Please try again.';
      showToast(errorMessage, 'error');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '11px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10,
    fontSize: 14, color: '#111', outline: 'none',
    fontFamily: 'inherit', transition: 'border-color 0.15s',
    background: anyBusy ? '#fafafa' : '#fff',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#9ca3af',
    marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px',
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.50)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 22, width: '100%', maxWidth: 460,
          boxShadow: '0 32px 80px rgba(0,0,0,0.22)',
          maxHeight: '94vh', overflowY: 'auto',
          animation: 'modalIn 0.22s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '18px 20px 14px', borderBottom: '1px solid #f3f4f6',
          position: 'sticky', top: 0, background: '#fff', zIndex: 2,
          borderRadius: '22px 22px 0 0',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Edit Listing</p>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 330 }}>{product.name}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                background: isSold ? 'rgba(239,68,68,0.10)' : 'rgba(22,163,74,0.10)',
                color: isSold ? '#dc2626' : '#16a34a',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: isSold ? '#dc2626' : '#16a34a', display: 'inline-block' }} />
                {isSold ? 'Sold' : 'Active'}
              </span>
              <span style={{ fontSize: 11, color: '#d1d5db' }}>·</span>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>ID #{product.id}</span>
            </div>
          </div>
          <button
            onClick={onClose} disabled={anyBusy}
            style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 34, height: 34, fontSize: 16, cursor: anyBusy ? 'not-allowed' : 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: 10, marginTop: 2 }}
          >✕</button>
        </div>

        <div style={{ padding: '16px 20px 26px' }}>
          {toast && (
            <div style={{
              marginBottom: 14, padding: '10px 14px', borderRadius: 11,
              background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
              color: toast.type === 'error' ? '#dc2626' : '#16a34a',
              fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 8,
              animation: 'fadeIn 0.2s ease',
            }}>
              <span style={{ fontSize: 16 }}>{toast.type === 'error' ? '⚠️' : '✅'}</span>
              {toast.msg}
            </div>
          )}

          {/* Media carousel */}
          <div style={{
            position: 'relative',
            borderRadius: 14,
            overflow: 'hidden',
            background: isCurrentVideo ? '#000' : '#f3f4f6',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 180,
            maxHeight: 340,
          }}>
            {isCurrentVideo ? (
              <video
                key={currentMedia}
                src={currentMedia}
                controls
                muted
                style={{
                  width: '100%',
                  maxHeight: 340,
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            ) : (
              <img
                key={currentMedia}
                src={currentMedia}
                alt={product.name}
                onError={handleImgError}
                style={{
                  width: '100%',
                  maxHeight: 340,
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            )}

            {/* Like counter badge */}
            <div style={{
              position: 'absolute', bottom: 10, right: 10,
              background: 'rgba(0,0,0,0.60)', color: '#fff',
              borderRadius: 99, padding: '5px 10px',
              fontSize: 12, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              {product.like_count ?? product.likes ?? 0} likes
            </div>

            {/* Navigation buttons */}
            {totalMedia > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  style={{
                    position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.50)', color: '#fff', border: 'none',
                    borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, transition: 'background 0.15s', zIndex: 3,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.75)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.50)'}
                >
                  ‹
                </button>

                <button
                  onClick={goToNext}
                  style={{
                    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.50)', color: '#fff', border: 'none',
                    borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, transition: 'background 0.15s', zIndex: 3,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.75)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.50)'}
                >
                  ›
                </button>

                {/* Media counter */}
                <div style={{
                  position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
                  background: 'rgba(0,0,0,0.60)', color: '#fff',
                  borderRadius: 99, padding: '5px 12px',
                  fontSize: 11, fontWeight: 600,
                }}>
                  {mediaIndex + 1} / {totalMedia}
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 22 }}>
            <div>
              <label style={labelStyle}>Listing Title</label>
              <input
                type="text" value={editName} disabled={anyBusy}
                onChange={e => setEditName(e.target.value)}
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#16a34a'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>

            <div>
              <label style={labelStyle}>Price</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', fontSize: 12, fontWeight: 800, color: '#9ca3af', pointerEvents: 'none' }}>KES</span>
                <input
                  type="number" value={editPrice} min="0" disabled={anyBusy}
                  onChange={e => setEditPrice(e.target.value)}
                  style={{ ...inputStyle, paddingLeft: 50, fontSize: 16, fontWeight: 700, color: '#16a34a' }}
                  onFocus={e => e.target.style.borderColor = '#16a34a'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Location</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                </span>
                <input
                  type="text" value={editLocation} disabled={anyBusy}
                  onChange={e => setEditLocation(e.target.value)}
                  placeholder="City or area"
                  style={{ ...inputStyle, paddingLeft: 33 }}
                  onFocus={e => e.target.style.borderColor = '#16a34a'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </div>
          </div>

          <button
            onClick={handleSave} disabled={anyBusy}
            style={{
              width: '100%', padding: 14, border: 'none', borderRadius: 13,
              background: savingPrice ? '#d1fae5' : 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
              color: savingPrice ? '#6b7280' : '#fff',
              fontSize: 15, fontWeight: 800, cursor: anyBusy ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginBottom: 10, transition: 'opacity 0.15s, transform 0.1s',
              opacity: (anyBusy && !savingPrice) ? 0.45 : 1,
              boxShadow: savingPrice ? 'none' : '0 4px 12px rgba(22,163,74,0.30)',
            }}
          >
            {savingPrice ? (
              <><Spinner color="#16a34a" /> Saving changes…</>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                Save Changes
              </>
            )}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 10px' }}>
            <div style={{ flex: 1, height: 1, background: '#f3f4f6' }} />
            <span style={{ fontSize: 10, color: '#d1d5db', fontWeight: 700, letterSpacing: '0.6px' }}>MORE OPTIONS</span>
            <div style={{ flex: 1, height: 1, background: '#f3f4f6' }} />
          </div>

          <button
            onClick={handleToggleStatus} disabled={anyBusy}
            style={{
              width: '100%', padding: '12px 14px', marginBottom: 8,
              border: `1.5px solid ${isSold ? 'rgba(22,163,74,0.35)' : 'rgba(245,158,11,0.40)'}`,
              borderRadius: 12,
              background: isSold ? 'rgba(22,163,74,0.05)' : 'rgba(245,158,11,0.05)',
              color: isSold ? '#15803d' : '#b45309',
              fontSize: 14, fontWeight: 700, cursor: anyBusy ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background 0.15s', opacity: (anyBusy && !togglingStatus) ? 0.45 : 1,
            }}
          >
            {togglingStatus ? (
              <><Spinner color={isSold ? '#15803d' : '#b45309'} /> Updating…</>
            ) : isSold ? (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Mark as Active
              </>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                Mark as Sold
              </>
            )}
          </button>

          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)} disabled={anyBusy}
              style={{
                width: '100%', padding: '12px 14px',
                border: '1.5px solid rgba(239,68,68,0.30)',
                borderRadius: 12, background: 'rgba(239,68,68,0.04)',
                color: '#dc2626', fontSize: 14, fontWeight: 700,
                cursor: anyBusy ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background 0.15s', opacity: anyBusy ? 0.45 : 1,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14H6L5 6"/>
                <path d="M10 11v6M14 11v6"/>
                <path d="M9 6V4h6v2"/>
              </svg>
              Delete Listing
            </button>
          ) : (
            <div style={{ border: '1.5px solid #fecaca', borderRadius: 13, padding: 16, background: '#fef2f2', animation: 'fadeIn 0.18s ease' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#dc2626' }}>Delete this listing?</span>
              </div>
              <p style={{ margin: '0 0 14px', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                <strong>"{product.name}"</strong> will be permanently removed. This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setConfirmDelete(false)} disabled={deleting}
                  style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete} disabled={deleting}
                  style={{
                    flex: 1.5, padding: '10px', border: 'none', borderRadius: 10,
                    background: deleting ? '#fca5a5' : 'linear-gradient(135deg,#ef4444,#dc2626)',
                    color: '#fff', fontSize: 13, fontWeight: 700,
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    boxShadow: deleting ? 'none' : '0 3px 10px rgba(239,68,68,0.35)',
                  }}
                >
                  {deleting ? <><Spinner color="#fff" /> Deleting…</> : '🗑 Yes, Delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SELLING TAB
// ══════════════════════════════════════════════════════════════════════════════

export default EditAdModal;

